import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  closeDialog,
  openDialog,
  selectDialogState,
} from '../store/dialogSlice';

export interface IUseDiagolData<T> {
  isOpened: boolean;
  context: T;
  onClose: () => void;
  /**
   * Open dialog with provided context (if needed)
   *
   * @param context - data to pass to dialog
   * @param ignoreOpened - if true, redux store will be updated even if dialog is already opened
   */
  onOpen: (context: T, ignoreOpened?: boolean) => void;
}

export function useDialog<TContext = unknown>(
  modalId: string,
): IUseDiagolData<TContext | undefined> {
  const dispatch = useDispatch();
  const dialogState = useSelector(selectDialogState);
  const isOpened = dialogState.currentModal === modalId;
  const context = dialogState.context as TContext | undefined;

  const handleClose = useCallback(() => dispatch(closeDialog()), [dispatch]);

  const handleOpen = useCallback(
    (contextParam?: TContext, ignoreOpened = false) => {
      if (isOpened && !ignoreOpened) {
        return;
      }

      dispatch(openDialog({ currentModal: modalId, context: contextParam }));
    },
    [dispatch, isOpened, modalId],
  );

  return {
    isOpened,
    context,
    onClose: handleClose,
    onOpen: handleOpen,
  };
}
